import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useCombobox } from "downshift";
import { VariableSizeList as List } from "react-window";
import { Paper, TextField, InputAdornment, IconButton, MenuItem } from "@mui/material";
import { ExpandMoreRounded, ExpandLessRounded } from "@mui/icons-material";

import { isSmallScreen } from "commons/src/helpers/ContextHelpers";

const desktopItemHeight = 42;
const mobileItemHeight = 76;
const maxDropdownHeight = 315;
const defaultListWidth = 600;
const largeMobileSize = "425px";

const FIRM_TYPE = "firm";

export function Combobox({ allItems, selectedItem, label, itemToString, onSelect, type }) {
    const [items, setItems] = useState([]);
    const [value, setValue] = useState("");
    const textInputRef = useRef();

    useEffect(() => {
        setItems(allItems);
    }, [allItems]);

    const {
        isOpen,
        getToggleButtonProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        getItemProps
    } = useCombobox({
        items,
        itemToString,
        selectedItem,
        onIsOpenChange: ({ isOpen, selectedItem }) => {
            if (isOpen && selectedItem) {
                setItems(allItems);
            }
        },
        onSelectedItemChange: ({ selectedItem }) => onSelect(selectedItem),
        onInputValueChange: ({ inputValue }) => {
            setValue(inputValue);
            setItems(
                allItems.filter(item =>
                    itemToString(item)?.toLowerCase().includes(inputValue?.toLowerCase())
                )
            );
        }
    });

    const showSuggestions = isOpen && allItems.length > 0;

    const itemHeight =
        isSmallScreen(largeMobileSize) && type === FIRM_TYPE ? mobileItemHeight : desktopItemHeight;

    return (
        <div {...getComboboxProps()}>
            <StyledTextField
                ref={textInputRef}
                fullWidth
                label={label}
                variant="outlined"
                value={value}
                inputProps={{ "data-testid": `${type}-input` }}
                InputProps={{
                    ...getInputProps({ refKey: "inputRef" }),
                    ...getToggleButtonProps(),
                    style: { paddingRight: 0 },
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label={
                                    isOpen ? `Collapse ${type} options` : `Expand ${type} options`
                                }
                                size="large"
                            >
                                {isOpen ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />

            <StyledPaper data-testid="suggestion-dropdown-list" {...getMenuProps()}>
                {showSuggestions ? (
                    <List
                        width={
                            textInputRef?.current?.getBoundingClientRect?.()?.width ??
                            defaultListWidth
                        }
                        height={items.length <= 7 ? items.length * itemHeight : maxDropdownHeight}
                        itemCount={items.length}
                        itemSize={() => itemHeight}
                        itemData={{
                            items,
                            getItemProps,
                            itemToString,
                            type
                        }}
                    >
                        {ItemRenderer}
                    </List>
                ) : null}
            </StyledPaper>
        </div>
    );
}

function ItemRenderer({ data, index, style }) {
    const { items, getItemProps, itemToString, type } = data;
    const item = items[index];

    return (
        <StyledMenuItem
            data-testid="suggestion-menu-item"
            className={`${type}-item`}
            {...getItemProps({
                style,
                item,
                index
            })}
        >
            {itemToString(item)}
        </StyledMenuItem>
    );
}

const StyledTextField = styled(TextField)`
    .MuiInputLabel-root {
        white-space: nowrap;
        max-width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .MuiInputLabel-root.Mui-focused {
        max-width: 100%;
    }
`;

const StyledMenuItem = styled(MenuItem)`
    @media (max-width: 425px) {
        &.firm-item {
            white-space: normal;
            overflow-wrap: break-word;
            line-height: 1.1rem;
        }
    }
`;

const StyledPaper = styled(Paper)`
    position: absolute;
    z-index: 10;
`;

ItemRenderer.propTypes = {
    data: PropTypes.shape({
        items: PropTypes.array,
        getItemProps: PropTypes.func,
        itemToString: PropTypes.func,
        type: PropTypes.string
    }),
    item: PropTypes.object,
    index: PropTypes.number,
    style: PropTypes.object
};

Combobox.propTypes = {
    allItems: PropTypes.arrayOf(
        PropTypes.shape({
            clientId: PropTypes.number,
            forename: PropTypes.string,
            surname: PropTypes.string,
            title: PropTypes.string
        })
    ),
    label: PropTypes.string,
    itemToString: PropTypes.func,
    onSelect: PropTypes.func,
    selectedItem: PropTypes.object,
    type: PropTypes.string
};
